import { ChangeDetectorRef, Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExerciseService } from 'src/app/_services/generatedServices';
import { ExerciseProgressionViewModelRead, ExerciseListViewModelRead, ExerciseProgressionViewModel, ExerciseViewModelRead } from 'src/app/_models/generatedModels';
import { timer } from 'rxjs';
import { ToasterService } from 'src/app/_services/toaster.service';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormArray } from '@angular/forms';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { dynamicSort } from 'src/app/_helpers/extensions.module';
import { map, switchMap } from 'rxjs/operators';
import { BreadcrumbsService } from 'src/app/_services/breadcrumbs.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormCanDeactivate } from 'src/app/_guards/canDeactivate.guard';
import { ExercisePreviewComponent } from '../../shared/exercise-preview/exercise-preview.component';
import { ExerciseAddModalComponent } from 'src/app/_components/exercise-add-modal/exercise-add-modal.component';


@Component({
  selector: 'bxl-add-edit-exercise-progression',
  templateUrl: 'add-edit-exercise-progression.component.html',
  styleUrls: ['add-edit-exercise-progression.component.scss']
})
export class AddEditExerciseProgressionComponent extends FormCanDeactivate implements OnInit {
  initialized = false;
  validating: boolean = false;
  public formGroup: FormGroup;
  submitComplete: Promise<{}> | undefined;
  listUrl: string = '/library/exercise-progressions';
  progressionId: number;
  progression: ExerciseProgressionViewModelRead;
  canEditGlobalLibrary: boolean = false;

  constructor(private route: ActivatedRoute, private exerciseService: ExerciseService, private auth: AuthenticationService, private router: Router, private fb: FormBuilder,
    private toastr: ToasterService, private breadcrumbs: BreadcrumbsService, private modalService: NgbModal, private cdr: ChangeDetectorRef, private elRef: ElementRef) {
      super();
    }

  ngOnInit(): void {
    this.progressionId = this.route.snapshot.params ? this.route.snapshot.params['id'] : null;
    this.breadcrumbs.SetSecondaryBreadcrumb('Exercise Progressions', this.listUrl, []);
    this.breadcrumbs.AppendBreadcrumb((this.progressionId ? 'Edit ' : 'Add ') + 'Exercise Progression', this.router.url, []);
    this.canEditGlobalLibrary = this.auth.canEditGlobalLibrary();

    if (this.progressionId) {
      this.exerciseService.getExerciseProgressionById(this.progressionId).subscribe(results => {
        this.progression = results;

        // make sure the progression id is valid for this user
        if (!this.progression.organizationId && !this.canEditGlobalLibrary) {
          this.router.navigate([this.listUrl]);
        }

        this.setupForm();
      });
    } else {
      this.progression = new ExerciseProgressionViewModelRead();
        this.setupForm();
    }
  }

  setupForm() {
    this.formGroup = this.fb.group({
      name: [this.progression.name, { validators: [Validators.required], asyncValidators: [this.validateNameNotTaken.bind(this)], updateOn: 'change' }],
      notes: [this.progression.notes],
      isHidden: [this.progression.isHidden || false],
      levels: this.fb.array([], [Validators.required, Validators.minLength(2)])
    });

    if (this.progressionId) {
      let levels = this.formGroup.get('levels') as FormArray;
      this.progression.levels.sort(dynamicSort('level')).forEach(level => {
        levels.push(this.fb.group({
          id: [level.id],
          level: [level.level],
          exerciseId: [level.exerciseId, Validators.required],
          newlyCreatedAndSelectedExerciseId: null
        }));
      });

    } else {
      this.onAddLevel(-1);
    }

    this.formGroup.markFormDirtyOnValueChange().subscribe();
    this.initialized = true;
  }

  onAddLevel(existingIndex: number) {
    let levels = this.formGroup.get('levels') as FormArray;
    levels.insert(existingIndex + 1, this.fb.group({
      id: [0],
      level: [existingIndex + 2],
      exerciseId: [null, Validators.required],
      newlyCreatedAndSelectedExerciseId: null
    }));

    this.updateLevelNumbers();
  }

  onAddLevelClone(existingIndex: number) {
    this.onAddLevel(existingIndex);

    let levels = this.formGroup.get('levels') as FormArray;
    this.onCreateExercise(levels.at(existingIndex).get('exerciseId').value, existingIndex + 1);
  }

  onExercisePreview(event: any, levelIndex: number) {
    let levels = this.formGroup.get('levels') as FormArray;
    const exerciseId = levels.at(levelIndex).get('exerciseId').value;
    if (exerciseId) {
      const modalRef = this.modalService.open(ExercisePreviewComponent, { size: 'lg' });
      modalRef.componentInstance.exerciseId = exerciseId;
      modalRef.result.then(
        (result) => {},
        (reason) => {}
      );
    }
  }

  onDeleteLevel(index: number) {
    let levels = this.formGroup.get('levels') as FormArray;
    levels.removeAt(index);
    this.updateLevelNumbers();
  }

  onMoveLevel(index: number, moveDirection: number) {
    let levels = this.formGroup.get('levels') as FormArray;
    let newIndex: number = index + moveDirection;
    if(newIndex === -1) {
      newIndex = levels.length - 1;
    } else if(newIndex == levels.length) {
      newIndex = 0;
    }

    const currentLevel = levels.at(index);
    levels.removeAt(index);
    levels.insert(newIndex, currentLevel)

    this.updateLevelNumbers();
  }

  updateLevelNumbers() {
    let levels = this.formGroup.get('levels') as FormArray;
    let levelNumber = 1;
    levels.controls.forEach(level => {
      level.get('level').setValue(levelNumber);
      levelNumber += 1;
    });
  }

  onCreateExercise(exerciseIdToModify: number | null, index: number) {
    const modalRef = this.modalService.open(ExerciseAddModalComponent, { size: 'lg', windowClass: 'modal-xl-custom', backdrop: 'static' });
    modalRef.componentInstance.exerciseIdToModify = exerciseIdToModify;
    modalRef.componentInstance.savedObject.subscribe((exercise: ExerciseViewModelRead) => {
      let levels = this.formGroup.get('levels') as FormArray;
      levels.at(index).get('exerciseId').setValue(exercise.id);
      levels.at(index).get('newlyCreatedAndSelectedExerciseId').setValue(exercise.id);
    });
    modalRef.result.then(
      (result) => {},
      (reason) => {}
    );
  }

  onExerciseChange(changedExercise: ExerciseListViewModelRead, index: number) {
    this.validating = true;
    let levels = this.formGroup.get('levels') as FormArray;
    levels.at(index).get('newlyCreatedAndSelectedExerciseId').setValue(null);

    if (changedExercise) {

      // detectChanges is necessary bc of Kendo bug with using Dialog inside valueChange
      this.cdr.detectChanges();
      // and this is necessary bc of a NgbModal bug  https://github.com/ng-bootstrap/ng-bootstrap/issues/1604   https://github.com/ng-bootstrap/ng-bootstrap/issues/1252
      (this.elRef.nativeElement.ownerDocument.activeElement as any)['blur'].apply(this.elRef.nativeElement.ownerDocument.activeElement, [blur]);

      let errorMessage: string = null;

      if (!this.auth.isSuperUser() && !changedExercise.organizationId) {
        errorMessage = 'You cannot select a RunDNA exercise to use in an exercise progression. Would you like to clone this exercise so that it can be used?';
        this.handleExerciseError(errorMessage, index, changedExercise.id);
        this.validating = false;
      } else {
        // see if this progression already has this exercise
        for (var i = 0; i < levels.controls.length; i++) {
          if (i != index) {
            if (levels.at(i).get('exerciseId').value == changedExercise.id) {
              errorMessage = 'This exercise is already being used in this progression. Would you like to clone this exercise so that it can be used?';
              this.handleExerciseError(errorMessage, index, changedExercise.id);
              break;
            }
          }
        }

        if (!errorMessage) {
          // check to see if exercise exists in another progression for org
          this.exerciseService.isExerciseInAnotherProgression(changedExercise.id, this.progressionId || 0).subscribe(isDuplicate => {
            this.validating = false;
            if (isDuplicate) {
              errorMessage = 'This exercise is already being used in another progression. Would you like to clone this exercise so that it can be used?';
              this.handleExerciseError(errorMessage, index, changedExercise.id);
            }
          });
        } else {
          this.validating = false;
        }
      }
    }
  }

  handleExerciseError(errorMessage: string, index: number, changedExerciseId: number) {
    let levels = this.formGroup.get('levels') as FormArray;
    this.toastr.confirmDialog(errorMessage, 'Clone Exercise', 'Clone Exercise', 'Cancel').subscribe(result => {
      levels.at(index).get('exerciseId').setValue(null);
      if (result) {
        this.onCreateExercise(changedExerciseId, index);
      }
    });
  }

  validateNameNotTaken(control: AbstractControl) {
    return timer(500).pipe(
      switchMap(() => this.exerciseService.isExerciseProgressionNameDuplicate(control.value, this.progressionId || 0)),
      map((res)  => {
        return res ? { nameTaken: true } : null;
      })
    );
  }

  cancel() {
    if (this.formGroup.dirty) {
      this.toastr.confirmDialog('Are you sure you want to discard changes?', 'Discard Changes').subscribe(result => {
        if (result) {
          this.router.navigate([this.listUrl]);
        }
      });
    } else {
      this.router.navigate([this.listUrl]);
    }
  }

  onSave() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllControlsDirty();
      this.toastr.error('Please fill out all required fields', 'Error');
      return;
    }

    this.formGroup.markAsPristine();
    this.submitComplete = new Promise((resetButton:any, reject) => {
      const formData: ExerciseProgressionViewModel = Object.assign({}, this.progression, this.formGroup.getRawValue());

      if (this.progressionId) {
        this.update(formData, resetButton);
      } else {
        // save the user who is creating the progression
        formData.userId = this.auth.user.id;
        this.add(formData, resetButton);
      }
    });
  }

  add(formData: any, resetButton: () => any) {
    this.exerciseService.addExerciseProgression(formData).subscribe((result) => {
      this.toastr.success('Progression Added', 'Success');
      resetButton();
      this.router.navigate([this.listUrl]);
    });
  }

  update(formData: any, resetButton: () => any) {
    const orginalExerciseIds = this.progression.levels.map(x => x.exerciseId);
    const updatedExerciseIds = formData.levels.map(x => x.exerciseId);
    if (!orginalExerciseIds.every(r => updatedExerciseIds.includes(r))) {
      this.toastr.confirmDialog('Warning: Removing exercises from the progression will affect any athlete that has changed their level to/from the removed exercise. The athlete will be reverted back to the default exercise level on the workout.', 'Confirm Update', 'Update Progression', 'Cancel', 290).subscribe(result => {
        if (result) {
          this.updateProgression(formData, resetButton);    
        } else {
          resetButton();
        }
      });
    } else {
      this.updateProgression(formData, resetButton);
    }
    
  }

  updateProgression(formData: any, resetButton: () => any) {
    this.exerciseService.updateExerciseProgression(this.progressionId, formData).subscribe((result) => {
      this.toastr.success('Progression Updated', 'Success');
      resetButton();
      this.router.navigate([this.listUrl]);
    });
  }
}
